import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, TrendingUp, Clock } from "lucide-react";
import { Reveal, SectionTitle } from "@/components/Reveal";
import { LIVE_PROVIDERS, genLiveRtp } from "@/data/mock";

const fmtTime = (d) =>
  d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

export const LiveRTP = () => {
  const [data, setData] = useState(() =>
    Object.fromEntries(LIVE_PROVIDERS.map((p) => [p.id, genLiveRtp()])),
  );
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    const iv = setInterval(() => {
      const target =
        LIVE_PROVIDERS[Math.floor(Math.random() * LIVE_PROVIDERS.length)].id;
      setData((d) => ({ ...d, [target]: genLiveRtp() }));
      setFlash(target);
      setTimeout(() => setFlash(null), 700);
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  return (
    <section id="rtp" className="relative py-20 md:py-28" data-testid="live-rtp-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <SectionTitle chapter="01 — Ao Vivo">
            Sinais <span className="text-gold-gradient">Ao Vivo</span>
          </SectionTitle>
          <div className="flex items-center gap-2 text-sm text-white/50">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF41] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF41]" />
            </span>
            Atualização automática a cada 3s
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5">
          {LIVE_PROVIDERS.map((p, i) => {
            const d = data[p.id];
            const isFlash = flash === p.id;
            return (
              <Reveal key={p.id} delay={i * 0.1}>
                <motion.div
                  animate={{
                    borderColor: isFlash
                      ? "rgba(245, 197, 66,0.6)"
                      : "rgba(245, 197, 66,0.2)",
                  }}
                  className="glass-gold p-6 md:p-7 h-full relative overflow-hidden"
                  data-testid={`live-card-${p.id}`}
                >
                  <div
                    className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl opacity-30"
                    style={{ background: p.accent }}
                  />
                  <div className="relative flex items-center justify-between">
                    <div>
                      <div className="font-display text-xl font-bold text-white">
                        {p.name}
                      </div>
                      <div className="text-xs text-white/40 mt-1 uppercase tracking-wider">
                        {p.tag}
                      </div>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs font-bold text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30 px-3 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00FF41]" />
                      Online
                    </span>
                  </div>

                  <div className="relative mt-7 flex items-end gap-2">
                    <motion.span
                      key={d.rtp}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="font-display text-4xl md:text-5xl font-bold tracking-tighter"
                      style={{ color: d.level.color }}
                    >
                      {d.rtp.toFixed(2)}%
                    </motion.span>
                    <span
                      className="mb-2 text-xs font-bold px-2.5 py-1 rounded-full border"
                      style={{
                        color: d.level.color,
                        borderColor: `${d.level.color}55`,
                        background: `${d.level.color}14`,
                      }}
                    >
                      {d.level.label}
                    </span>
                  </div>
                  <div className="text-xs text-white/40 mt-1">Sinal Atual</div>

                  <div className="relative mt-6 grid grid-cols-2 gap-3 pt-5 border-t border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#F5C542]" />
                      <div>
                        <div className="text-[10px] text-white/40 uppercase">
                          Volatilidade
                        </div>
                        <div className="text-sm text-white font-medium">
                          {d.vol}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#F5C542]" />
                      <div>
                        <div className="text-[10px] text-white/40 uppercase">
                          Atualizado
                        </div>
                        <div className="text-sm text-white font-medium tabular-nums">
                          {fmtTime(d.updated)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Activity className="absolute bottom-5 right-5 w-4 h-4 text-white/10" />
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
